import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { Settings } from '../../models/settings';
import { User } from '../../models/user';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  constructor(
    private userService: UserService,
    private authService: AuthService
  ) {}

  async getSettings(): Promise<Settings> {
    // Check if user is logged in first, to avoid unnecessary failing 401 API calls.
    // If not, try to get settings from local storage.
    if (!this.authService.isLoggedIn.value) {
      return this.getLocalOrDefaultSettings();
    }

    // Try to get current user settings
    try {
      const user = await this.userService.getCurrentUserAsync();
      return new Settings(user.settings);
    } catch {
      // No user was found or no user is logged in.
      // Check if settings can be found in local storage.
      return this.getLocalOrDefaultSettings();
    }
  }

  private getLocalOrDefaultSettings(): Settings {
    console.log('No user is logged in, so locally stored settings (if any) are used.');
    const localSettingsJson = localStorage.getItem('settings');
    if (!localSettingsJson) {
      return new Settings();
    }

    return new Settings(JSON.parse(localSettingsJson));
  }

  async saveSettings(settings: Settings): Promise<Settings> {
    // Get current user to save settings to.
    let user: User;

    try {
      user = await this.userService.getCurrentUserAsync();
      user.settings = settings;
    } catch {
      // No current user found.
      // Save settings to local storage.
      console.log('No user is logged in, so settings are saved to local storage.');
      localStorage.setItem('settings', JSON.stringify(settings));
      return settings;
    }

    try {
      const updatedUser = await this.userService.updateUserAsync(user);
      return updatedUser.settings;
    } catch {
      throw new Error('An error occurred while trying to save settings.');
    }
  }
}
